import React, { useEffect, useState } from "react";
import { Plus, Clock, Trash2, Save, X, TrendingUp, TrendingDown } from "lucide-react";
import { dataApi, formatApiError } from "../api/client";

/**
 * PricingBandsView — CRUD de franjas horarias con multiplicador global.
 *
 * Cada franja define ventana horaria (start_hour → end_hour), multiplicador
 * (0.5–3.0), días de semana opcionales, y aplica automáticamente al
 * `estimated_fare` de todos los rides creados durante esa ventana.
 *
 * Si la franja atraviesa medianoche (end ≤ start) → válida (ej. 22→6).
 * Si múltiples franjas activas se solapan → se usa la de multiplicador MÁS ALTO.
 */
const DAY_NAMES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const emptyBand = () => ({
  name: "",
  start_hour: 22,
  end_hour: 6,
  multiplier: 1.2,
  days_of_week: [],
  is_active: true,
  notes: "",
});

export default function PricingBandsView() {
  const [bands, setBands] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | band
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [b, a] = await Promise.all([dataApi.pricingBands(), dataApi.pricingBandActive()]);
      setBands(Array.isArray(b) ? b : []);
      setActive(a);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    setErr("");
    const payload = {
      name: form.name.trim(),
      start_hour: Number(form.start_hour),
      end_hour: Number(form.end_hour),
      multiplier: Number(form.multiplier),
      days_of_week: form.days_of_week,
      is_active: form.is_active,
      notes: form.notes || null,
    };
    try {
      if (form.id) await dataApi.pricingBandUpdate(form.id, payload);
      else await dataApi.pricingBandCreate(payload);
      setEditing(null);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta franja?")) return;
    try { await dataApi.pricingBandDelete(id); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div data-testid="pricing-bands-view" className="max-w-6xl mx-auto p-6 space-y-5">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted flex items-center gap-2">
            <Clock className="w-3 h-3" /> Precio dinámico
          </div>
          <h1 className="font-display text-2xl font-semibold text-mdd-text tracking-tight">Franjas horarias</h1>
          <p className="text-sm text-mdd-muted mt-1 max-w-2xl">
            Recargos o descuentos globales aplicados automáticamente sobre el precio
            de TODOS los rides según la hora del viaje (o de la reserva, si es programada).
          </p>
        </div>
        <button
          data-testid="pricing-band-new"
          onClick={() => setEditing({ ...emptyBand(), id: null })}
          className="mdd-btn mdd-btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nueva franja
        </button>
      </header>

      {/* Banner: multiplicador aplicándose AHORA */}
      {active && (
        <div
          data-testid="pricing-active-now"
          className={`mdd-card p-4 border-l-4 flex items-center gap-3 ${
            active.multiplier > 1 ? "border-orange-500 bg-orange-500/5"
            : active.multiplier < 1 ? "border-emerald-500 bg-emerald-500/5"
            : "border-slate-500 bg-slate-500/5"
          }`}
        >
          {active.multiplier > 1 ? <TrendingUp className="w-5 h-5 text-orange-400" />
            : active.multiplier < 1 ? <TrendingDown className="w-5 h-5 text-emerald-400" />
            : <Clock className="w-5 h-5 text-slate-400" />}
          <div className="flex-1">
            <div className="font-medium text-mdd-text">
              Ahora aplica ×{active.multiplier.toFixed(2)}
              {active.band && <> — <span className="font-semibold">{active.band.name}</span></>}
            </div>
            <div className="text-xs text-mdd-muted mt-0.5">
              {active.multiplier === 1
                ? "Sin recargos ni descuentos activos en este momento"
                : active.multiplier > 1
                ? `Recargo del ${((active.multiplier - 1) * 100).toFixed(0)}%`
                : `Descuento del ${((1 - active.multiplier) * 100).toFixed(0)}%`}
            </div>
          </div>
        </div>
      )}

      {err && <div className="mdd-card p-3 border-l-4 border-red-500 bg-red-500/5 text-sm text-red-300">{err}</div>}

      {loading ? (
        <div className="mdd-card p-8 text-center text-mdd-muted">Cargando…</div>
      ) : bands.length === 0 && !editing ? (
        <div className="mdd-card p-8 text-center text-mdd-muted">
          Aún no hay franjas horarias. Crea la primera para empezar a variar precios por hora.
        </div>
      ) : (
        <div className="grid gap-3">
          {bands.map((b) => (
            <BandRow key={b.id} band={b} onEdit={() => setEditing(b)} onDelete={() => remove(b.id)} />
          ))}
        </div>
      )}

      {editing && (
        <BandEditor
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function BandRow({ band, onEdit, onDelete }) {
  const isBoost = band.multiplier > 1;
  const isDiscount = band.multiplier < 1;
  const days = band.days_of_week?.length ? band.days_of_week.map((d) => DAY_NAMES[d - 1]).join(", ") : "Todos los días";
  return (
    <div data-testid={`pricing-band-row-${band.id}`} className="mdd-card p-4 flex items-center gap-4 flex-wrap">
      <div
        className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
          isBoost ? "bg-orange-500/20 text-orange-300"
          : isDiscount ? "bg-emerald-500/20 text-emerald-300"
          : "bg-slate-500/20 text-slate-300"
        }`}
      >
        ×{band.multiplier.toFixed(1)}
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="font-medium text-mdd-text flex items-center gap-2">
          {band.name}
          {!band.is_active && <span className="text-[10px] px-2 py-0.5 rounded bg-slate-500/20 text-slate-400 font-mono uppercase">Inactiva</span>}
        </div>
        <div className="text-xs text-mdd-muted mt-0.5 font-mono">
          {fmtHour(band.start_hour)} – {fmtHour(band.end_hour)}
          {band.end_hour <= band.start_hour && <span className="ml-1 text-orange-400">(cruza medianoche)</span>}
          {" · "}{days}
        </div>
        {band.notes && <div className="text-xs text-mdd-muted mt-1 italic">{band.notes}</div>}
      </div>
      <div className="flex gap-2">
        <button onClick={onEdit} data-testid={`pricing-band-edit-${band.id}`} className="mdd-btn mdd-btn-ghost text-sm px-3">Editar</button>
        <button onClick={onDelete} data-testid={`pricing-band-delete-${band.id}`} className="mdd-btn mdd-btn-ghost text-sm px-3 text-red-400 hover:text-red-300">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function BandEditor({ initial, onCancel, onSave }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDay = (iso) => {
    const has = form.days_of_week.includes(iso);
    set("days_of_week", has ? form.days_of_week.filter((d) => d !== iso) : [...form.days_of_week, iso].sort());
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="mdd-card p-6 max-w-lg w-full space-y-4" data-testid="pricing-band-editor">
        <div className="flex items-start justify-between">
          <h3 className="font-medium text-mdd-text text-lg">{form.id ? "Editar franja" : "Nueva franja"}</h3>
          <button onClick={onCancel} className="text-mdd-muted"><X className="w-5 h-5" /></button>
        </div>

        <Field label="Nombre">
          <input data-testid="band-name" className="mdd-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ej. Recargo nocturno" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Hora inicio (0–24)">
            <input data-testid="band-start" type="number" min="0" max="24" step="0.5" className="mdd-input" value={form.start_hour} onChange={(e) => set("start_hour", e.target.value)} />
          </Field>
          <Field label="Hora fin (0–24)">
            <input data-testid="band-end" type="number" min="0" max="24" step="0.5" className="mdd-input" value={form.end_hour} onChange={(e) => set("end_hour", e.target.value)} />
          </Field>
        </div>
        {Number(form.end_hour) <= Number(form.start_hour) && (
          <div className="text-xs text-orange-400 font-mono">Cruza medianoche: aplica de {fmtHour(form.start_hour)} a 24:00 y de 00:00 a {fmtHour(form.end_hour)}.</div>
        )}

        <Field label={`Multiplicador (${((Number(form.multiplier) - 1) * 100).toFixed(0)}% ${Number(form.multiplier) >= 1 ? "recargo" : "descuento"})`}>
          <input data-testid="band-multiplier" type="number" min="0.5" max="3" step="0.05" className="mdd-input" value={form.multiplier} onChange={(e) => set("multiplier", e.target.value)} />
        </Field>

        <Field label="Días (vacío = todos)">
          <div className="flex flex-wrap gap-2">
            {DAY_NAMES.map((d, i) => {
              const iso = i + 1;
              const active = form.days_of_week.includes(iso);
              return (
                <button
                  key={iso}
                  data-testid={`band-day-${iso}`}
                  onClick={() => toggleDay(iso)}
                  className={`px-3 py-1.5 rounded-full text-xs font-mono ${active ? "bg-mdd-accent text-black font-semibold" : "bg-white/5 text-mdd-muted"}`}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Notas (opcional)">
          <input data-testid="band-notes" className="mdd-input" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
        </Field>

        <label className="flex items-center gap-2 text-sm text-mdd-text">
          <input data-testid="band-active" type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} />
          Activa
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button data-testid="band-cancel" onClick={onCancel} className="mdd-btn mdd-btn-ghost">Cancelar</button>
          <button data-testid="band-save" onClick={() => onSave(form)} disabled={!form.name.trim()} className="mdd-btn mdd-btn-primary flex items-center gap-2">
            <Save className="w-4 h-4" /> Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted mb-1">{label}</div>
      {children}
    </label>
  );
}

function fmtHour(h) {
  const val = Number(h);
  const hh = Math.floor(val).toString().padStart(2, "0");
  const mm = Math.round((val - Math.floor(val)) * 60).toString().padStart(2, "0");
  return `${hh}:${mm}`;
}
